import React, { useCallback, useEffect, useState } from "react";
import { gql, useMutation, useQuery } from "@apollo/client";
import { ACCEPTED, active, REJECTED } from "../LockClient/active";
import { ResultTypes } from "../LockClient/result";
import useField from "./useField";
import Error from "./Error";

export default () => {
  const { loading, error, data } = useQuery(
    gql`
      query {
        me {
          username
          hasOtp
          deviceType
        }
      }
    `,
    {
      fetchPolicy: "cache-and-network",
    },
  );
  if (error) return <span>{error}</span>;
  if (loading) return <span>Loading...</span>;
  return data?.me?.hasOtp ? <Active /> : <NoOTP />;
};

const Active = () => {
  const [active, setActive] = useState(false);
  const [result, setResult] = useState("unknown");
  const [target, targetError, targetProps] = useField((value) =>
    value ? null : "Must provide a target",
  );

  return (
    <div>
      <span id="result" style={{ display: "none" }}>
        {result}
      </span>
      <p>
        <label>
          Target
          <input disabled={active} placeholder="Target..." {...targetProps} />
        </label>
        {targetError && <Error>{targetError}</Error>}
      </p>
      <p>
        <button disabled={!target || active} onClick={() => setActive(true)}>
          Start
        </button>
      </p>
      {active && target && (
        <Progress
          target={target}
          onDone={(success) => {
            setResult(success ? "success" : "failure");
          }}
          onReset={() => setActive(false)}
        />
      )}
    </div>
  );
};

const Progress = ({
  target,
  onDone,
  onReset,
}: {
  target: string;
  onDone: (success: boolean) => void;
  onReset: () => void;
}) => {
  const [done, setLocalDone] = useState(false);
  const setDone = useCallback(
    (success: boolean) => {
      setLocalDone(true);
      onDone(success);
    },
    [onDone],
  );
  const [log, setLog] = useState<Array<string>>(["Initializing..."]);
  const addLog = (entry: string) => setLog((logs) => [...logs, entry]);
  const [remoteInit, { called, data }] = useMutation(gql`
    mutation Active($target: String!) {
      active(target: $target) {
        url
        requestId
      }
    }
  `);
  const [remoteConfirm] = useMutation(
    gql`
      mutation Confirm($digits: String!, $requestId: String!) {
        confirm(digits: $digits, requestId: $requestId) {
          success
        }
      }
    `,
    {
      onCompleted: (data) => {
        addLog(data.confirm.success ? "Success!" : "Failure!");
        setDone(data.confirm.success);
      },
      onError: (error) => {
        addLog(error.message);
        setDone(false);
      },
    },
  );
  useEffect(() => {
    if (called) return;
    remoteInit({ variables: { target } });
  }, [called, remoteInit, target]);

  useEffect(() => {
    if (!data?.active?.url) return;
    addLog("Connecting...");

    async function inner() {
      const result = await active(data.active.url);
      addLog("Confirming...");
      if (result.type === ResultTypes.Err) {
        addLog(`ERROR: ${result.kind}: ${result.info?.toString() || ""}`);
        return setDone(false);
      }
      switch (result.value.type) {
        case ACCEPTED:
          return remoteConfirm({
            variables: {
              digits: result.value.digits,
              requestId: data.active.requestId,
            },
          });
        case REJECTED:
          addLog("Rejected");
          return setDone(false);
      }
    }

    inner();
  }, [data, setDone, remoteConfirm]);

  return (
    <>
      <ul>
        {log.map((entry) => (
          <li key={entry}>{entry}</li>
        ))}
      </ul>
      {done && <button onClick={() => onReset()}>Reset</button>}
    </>
  );
};

const NoOTP = () => <span>You must set up OTP first.</span>;
